import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import reviewsPricing from '@klv/shared/pricing/static/reviews.json';
import cdpPricing from '@klv/shared/pricing/static/cdp.json';
import helpdeskTicketsPricing from '@klv/shared/pricing/static/helpdeskTickets.json';
import aiConversationsPricing from '@klv/shared/pricing/static/aiConversations.json';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import Slider from '@klv/molecules/slider';
import Button from '@klv/atoms/button';
import { FeaturesList } from '../features-list';
import * as styles from './styles/platform-card.module.css';
import type { IAnalyticsItem, IPlatformCard } from './types';

interface ICdpPlan {
  profiles: number;
  total_monthly_cost: number;
}

interface IReviewsPlan {
  plan_price: number;
  monthly_orders: number;
}

type TCdpPlans = keyof typeof cdpPricing['cdp'];

type TReviewsPlans = keyof typeof reviewsPricing['reviews'];

interface IHelpdeskPlan {
  price: number;
  tickets: number;
}

type THelpdeskPlans = keyof typeof helpdeskTicketsPricing['helpdeskTickets'];

const PlatformCard = ({
  description,
  featuresList,
  name,
  data,
  subText,
  subTextInfo,
  subTextInfoHeader,
  pricingDescription,
  activeProfiles,
  pillText,
  cardButtonText,
  cardButtonUrl,
  ...props
}: IPlatformCard) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;


  const findAnalyticsInfo = useCallback((analyticsArray: IAnalyticsItem[], inputValue: number) => {
    return analyticsArray.find((item) => item.value === inputValue) || null;
  }, [name]);

  const result = useMemo(
    () =>
      (name === 'Marketing Analytics' || name === 'Customer Hub') && activeProfiles !== undefined
        ? findAnalyticsInfo(data, activeProfiles)
        : null,
    [name, activeProfiles, data, findAnalyticsInfo],
  );

  const [reviewsOrders, setReviewsOrders] = useState(250);
  const [cdpProfiles, setCdpProfiles] = useState(100000);
  const [aiConversations, setAiConversations] = useState(500);
  const [tickets, setTickets] = useState(1000);

  const reviewsPlans = reviewsPricing.reviews;
  const cdpPlans = cdpPricing.cdp;
  const helpdeskPlans = helpdeskTicketsPricing.helpdeskTickets;
  const aiConversationsPlans = aiConversationsPricing.aiConversations;
  const selectedCdpPlan: ICdpPlan = cdpPlans[cdpProfiles.toString() as TCdpPlans];
  const selectedReviewsPlan: IReviewsPlan = reviewsPlans[reviewsOrders.toString() as TReviewsPlans];
  const selectedHelpdeskPlan: IHelpdeskPlan = helpdeskPlans[tickets.toString() as THelpdeskPlans];
  const selectedAiConversationsPlan = aiConversationsPlans[aiConversations.toString() as keyof typeof aiConversationsPlans];

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleClick = () => {
    gtmBasicEvent({
      category: 'platform card',
      action: 'demo cta',
      label: name.toLowerCase(),
    });
    return true;
  };

  // Helper function to get current slider value
  const getSliderValue = () => {
    switch (name) {
      case 'Advanced Klaviyo Data Platform':
        return cdpProfiles;
      case 'Customer Agent':
        return aiConversations;
      case 'Helpdesk':
        return tickets;
      default:
        return reviewsOrders;
    }
  };

  // Helper function to calculate price
  const calculatePrice = useCallback(() => {
    switch (name) {
      case 'Advanced Klaviyo Data Platform':
        return selectedCdpPlan.total_monthly_cost;
      case 'Customer Agent':
        return selectedAiConversationsPlan?.price;
      case 'Helpdesk':
        return selectedHelpdeskPlan.price;
      default:
        return selectedReviewsPlan.plan_price;
    }
  }, [name, selectedCdpPlan, selectedAiConversationsPlan, selectedHelpdeskPlan, selectedReviewsPlan]);

  // Helper function to handle slider changes
  const handleSliderChange = (value: number) => {
    switch (name) {
      case 'Advanced Klaviyo Data Platform':
        setCdpProfiles(value);
        break;
      case 'Customer Agent':
        setAiConversations(value);
        break;
      case 'Helpdesk':
        setTickets(value);
        break;
      default:
        setReviewsOrders(value);
    }
  };

  return (
    <div className={styles.platformCard} {...props}>
      <div className={styles.cardContents}>
        <div className={styles.planNameContainer}>
          {pillText && <div className={styles.pillTextContainer}>
            <div className={styles.pillText}>
              {pillText}
            </div>
          </div>}
          <div className={styles.planName}>{name}</div>
          <div className={styles.priceDescription}>
            {pricingDescription}
          </div>
        </div>

        {name !== 'Marketing Analytics' && name !== 'Customer Hub' ? (
          <>
            <div className={styles.priceWrapper}>
                  <div className={styles.price}>
                    {formatPrice(calculatePrice())}
                    <div className={styles.timePeriod}>
                      {t('organisms_pricing_per_month')}
                    </div>
                  </div>
            </div>
            <div className={styles.sliderWrapper}>
              <Slider
                subText={subText}
                range={data}
                variant="platform"
                subTextInfoHeader={subTextInfoHeader}
                subTextInfo={subTextInfo}
                value={getSliderValue()}
                onChange={handleSliderChange}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.priceWrapper}>
                <div className={styles.price}>
                  {formatPrice(Number(result?.price))}
                  <div className={styles.timePeriod}>{t('organisms_pricing_per_month')}</div>
                </div>
            </div>
            <div className={styles.priceInfo}>
              {result && result.value < 250000 ? (
                <div className={styles.activeProfiles}>
                  <div>{`${(result?.label || '0')} ${t('organisms_pricing_contacts')}`}</div>
                  <div className={styles.activeProfilesSubText}><span className={styles.asterisk}>*</span>{subText}</div>
                </div>
              ) : (
                <div className={styles.activeProfiles}>
                  {t('organisms_pricing_plans_paidCardContent_above250kContacts')}
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.divider} />
        <div className={styles.description}>{description}</div>
        <div className={styles.divider} />

        <div className={styles.buttonContainer}>
          <Button
            className={styles.button}
            text={cardButtonText || t('organisms_navigation_dataActions_getADemo_button_label')}
            url={cardButtonUrl || t('organisms_navigation_dataActions_getADemo_button_href')}
            buttonColor="dark"
            action={handleClick}
          />
        </div>

        <div className={styles.featuresListContainer}>
          {featuresList?.map((feature) => (
            <FeaturesList key={feature.listText} listIcon={feature.listIcon} listText={feature.listText} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlatformCard;

